/**
 * The composer's attachment tray: the clip that opens the picker, and a row per
 * file underneath it.
 *
 * The tray only exists once something is in it. An empty strip above the send
 * button would be a permanent reminder of a feature most messages do not use,
 * and the clip in the footer already says the option is there.
 *
 * A refused file keeps its row and says why. The alternative - dropping it
 * silently - makes the picker look broken: the operator chose a file, watched
 * nothing happen, and has no way to tell a rejected file from a slow one.
 */
import { useRef, useState } from "react";
import { AlertCircle, Images, Laptop, Loader2, Paperclip, X } from "lucide-react";
import { cn } from "@wa/ui";
import { useT } from "../../../../i18n/I18nProvider";
import { MediaPicker } from "../../../../components/MediaPicker";
import { IconBtn } from "../../../ui";
import { fmtBytes } from "../../../ui/format";
import { Pop, useClosePops } from "../../../shell/Pop";
import type { ComposerAttachments as Tray } from "./useComposerAttachments";

/**
 * The clip: two sources, one menu.
 *
 * It used to open the file dialog straight away, which is right until the file
 * you want is already in the media library - and then the only way to attach it
 * is to download it first and pick it back up, which produces a second copy of
 * a picture the office already owns.
 *
 * The library reaches the message WITHOUT the bytes passing through the browser:
 * the picker hands over a media id, the server copies the object. Fetching the
 * URL here and rebuilding a File would be slower, and it would not work at all
 * for anything the browser has no right to read.
 *
 * `siteId` is what the picker needs, and it is absent for an operator whose
 * account carries no host site. The menu then has one entry, which is exactly
 * what it had before this change.
 */
export function AttachButton({
  tray,
  siteId,
  disabled,
}: {
  tray: Tray;
  siteId?: string | null;
  disabled?: boolean;
}) {
  const t = useT();
  const input = useRef<HTMLInputElement>(null);
  const [library, setLibrary] = useState(false);
  const full = tray.files.filter((f) => f.status !== "error").length >= tray.limits.maxFiles;
  const title = full ? t("compose.attachFull", { count: tray.limits.maxFiles }) : t("compose.attach");

  return (
    <>
      <input
        ref={input}
        type="file"
        multiple
        className="hidden"
        onChange={(e) => {
          tray.add(e.target.files);
          // Cleared so that picking the SAME file again still fires `change`.
          // Without this, removing a file and re-adding it does nothing, which
          // reads as the button having stopped working.
          e.target.value = "";
        }}
      />

      <Pop
        trigger={(open) => (
          <IconBtn
            className={cn(open && "is-on")}
            title={title}
            aria-label={t("compose.attach")}
            aria-expanded={open}
            disabled={disabled || full}
          >
            <Paperclip className="size-4" />
          </IconBtn>
        )}
      >
        <AttachMenu
          onDisk={() => input.current?.click()}
          onLibrary={siteId ? () => setLibrary(true) : undefined}
        />
      </Pop>

      {siteId && (
        <MediaPicker
          open={library}
          siteId={siteId}
          onClose={() => setLibrary(false)}
          onPick={(asset) => {
            tray.addFromMedia(asset);
            setLibrary(false);
          }}
        />
      )}
    </>
  );
}

function AttachMenu({ onDisk, onLibrary }: { onDisk: () => void; onLibrary?: () => void }) {
  const t = useT();
  const close = useClosePops();

  const item = "flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-[12px] font-[550] hover:bg-[var(--crm-sunk)]";

  return (
    <div className="w-52 p-1">
      <button
        type="button"
        className={item}
        onClick={() => {
          close();
          // After the menu is gone: opening the file dialog from inside a
          // closing popover leaves the popover's focus trap holding the click.
          onDisk();
        }}
      >
        <Laptop className="size-4 flex-none text-muted-foreground" />
        {t("compose.attachFromDisk")}
      </button>
      {onLibrary && (
        <button
          type="button"
          className={item}
          onClick={() => {
            close();
            onLibrary();
          }}
        >
          <Images className="size-4 flex-none text-muted-foreground" />
          {t("compose.attachFromLibrary")}
        </button>
      )}
    </div>
  );
}

/** The rows themselves. Renders nothing at all when the tray is empty. */
export function AttachmentTray({ tray }: { tray: Tray }) {
  const t = useT();
  if (tray.files.length === 0) return null;

  return (
    // Capped and scrolled rather than left to grow. Ten files at full height
    // would be 360px of tray in a 560px window, which leaves the message itself
    // a two-line slot - the wrong way round for a thing that is still being
    // written. `flex-none` so the cap is honoured instead of the row stretching.
    <div className="flex max-h-[168px] flex-none flex-col gap-1 overflow-y-auto border-t border-[var(--crm-hair)] px-4 py-2.5">
      {tray.files.map((f) => {
        const failed = f.status === "error";
        return (
          <div
            key={f.localId}
            className={cn(
              "flex items-center gap-2 rounded-lg px-2 py-1.5 text-[12px]",
              failed ? "bg-red-500/8 text-red-600 dark:text-red-300" : "bg-[var(--crm-sunk)]",
            )}
          >
            {f.status === "uploading" ? (
              <Loader2 className="size-3.5 flex-none animate-spin text-muted-foreground" />
            ) : failed ? (
              <AlertCircle className="size-3.5 flex-none" />
            ) : (
              <Paperclip className="size-3.5 flex-none text-muted-foreground" />
            )}
            {/* `min-w-0` is what lets the name truncate instead of pushing the
                size and the remove button out of a 466px window. */}
            <span className="crm-trunc min-w-0 flex-1 font-[550]">{f.name}</span>
            <span className="flex-none text-[11px] tabular-nums text-muted-foreground">
              {failed ? t(`compose.attachError.${f.error}`, {
                size: fmtBytes(tray.limits.maxBytes),
                total: fmtBytes(tray.limits.maxTotalBytes),
                count: tray.limits.maxFiles,
              }) : fmtBytes(f.size)}
            </span>
            <button
              type="button"
              className="flex-none rounded-full p-1 text-muted-foreground hover:bg-[var(--crm-surface)] hover:text-foreground"
              onClick={() => tray.remove(f.localId)}
              title={t("compose.attachRemove", { name: f.name })}
              aria-label={t("compose.attachRemove", { name: f.name })}
            >
              <X className="size-3.5" />
            </button>
          </div>
        );
      })}
      {/* One line for the whole message, because the caps that actually refuse
          a send are per-message and no single row can show one being reached. */}
      <p className="px-2 pt-0.5 text-[11px] text-muted-foreground">
        {t("compose.attachTotal", {
          size: fmtBytes(tray.totalBytes),
          max: fmtBytes(tray.limits.maxTotalBytes),
        })}
      </p>
    </div>
  );
}
